import React, { type Ref, useMemo } from "react";

import {
  faCheck,
  faChevronDown,
  faRefresh,
  faTable,
  faTimes,
} from "@fortawesome/pro-regular-svg-icons";
import { useSearchParams } from "react-router-dom";

import { Divider } from "src/base-components/Divider";
import { FixedPositionedDropdown } from "src/base-components/FixedPositionedDropDown";
import { TemplateVersionName } from "src/cases/templates/TemplateVersionName";
import { getTemplateIcon } from "src/cases/templates/utils";
import {
  JsonApiResourceObjectCaseTemplateAttributesBaseModel,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
} from "src/clients/case-management";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { ArchivedPill, DraftPill, PublishedPill } from "src/flow/Pills";
import { SHOW_REFRESH_TOOLTIP_ON_OPEN } from "src/schema/schemaEditor/useErrorToasts";

export const LATEST_VERSION_ID = "latest";

type DropdownTemplate = JsonApiResourceObjectCaseTemplateAttributesBaseModel & {
  versions?: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel[];
};

export type DropdownSelection = {
  templateId: string;
  versionId: string;
};

type DropdownElementValue =
  | { template: DropdownTemplate; kind: "latest" }
  | {
      template: DropdownTemplate;
      kind: "pinned";
      version: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel;
    };

type Props = {
  options: DropdownTemplate[];
  selected?: DropdownSelection;
  onSelect: (selection: DropdownSelection) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
  onReset?: () => void;
  dataLoc?: string;
  selectedTemplateName?: React.ReactNode;
  selectedVersionName?: React.ReactNode;
  onRefresh?: (selection: DropdownSelection) => void;
  buttonRef?: Ref<HTMLDivElement>;
  publishedOnly?: boolean;
  showLatestVersionOption?: boolean;
};

const getFilteredVersions = (
  versions: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel[],
  publishedOnly: boolean,
) => {
  if (!publishedOnly) return versions;
  return versions.filter(
    (v) => v.attributes.publish_state === "published" && !v.attributes.archived,
  );
};

export const CaseTemplateDropdown: React.FC<Props> = ({
  options: templates,
  selected,
  onSelect,
  placeholder = "Select a case template",
  disabled = false,
  className,
  error,
  onReset,
  dataLoc,
  selectedTemplateName,
  selectedVersionName,
  onRefresh,
  buttonRef,
  publishedOnly = false,
  showLatestVersionOption = false,
}) => {
  const sections = templates.map((template) => ({
    key: template.id,
    displayText: (
      <div className="px-1">
        <div className="flex items-center">
          <Icon
            icon={getTemplateIcon(template.attributes.icon) ?? faTable}
            size="xs"
          />
          <p className="ml-2 font-inter-semibold-13px text-gray-800">
            {template.attributes.template_name}
          </p>
        </div>
        {template.attributes.description && (
          <p className="mt-2 ml-7 break-words pr-2 font-inter-normal-12px text-gray-600">
            {template.attributes.description}
          </p>
        )}
      </div>
    ),
  }));

  const elements = useMemo(() => {
    const result: {
      key: string;
      value: DropdownElementValue;
      sectionKey: string;
      helpText: string;
    }[] = [];

    for (const template of templates) {
      if (showLatestVersionOption) {
        result.push({
          key: `${template.id}_${LATEST_VERSION_ID}`,
          value: { template, kind: "latest" },
          sectionKey: template.id,
          helpText: "Always use the last edited published version",
        });
      }

      const versions = getFilteredVersions(
        template.versions || [],
        publishedOnly,
      );

      for (const version of versions) {
        result.push({
          key: `${template.id}_${version.id}`,
          value: { template, kind: "pinned", version },
          sectionKey: template.id,
          helpText: version.attributes.version_name,
        });
      }
    }

    return result;
  }, [templates, publishedOnly, showLatestVersionOption]);

  const selectedKey = selected
    ? `${selected.templateId}_${selected.versionId}`
    : undefined;

  return (
    <FixedPositionedDropdown
      className={className}
      dataLoc={dataLoc}
      disabled={disabled}
      elements={elements}
      error={error}
      itemsClassNames="w-[var(--button-width)]"
      renderButtonValue={(value, isOpen) => (
        <div
          ref={buttonRef}
          className="flex h-8 w-full items-center py-1.5 pr-2 pl-3"
        >
          {(() => {
            if (selected && value?.kind === "latest") {
              return (
                <>
                  <Icon
                    icon={
                      getTemplateIcon(value.template.attributes.icon) ?? faTable
                    }
                    size="xs"
                  />
                  <span className="ml-1 truncate text-gray-800">
                    {`${value.template.attributes.template_name} (latest version)`}
                  </span>
                </>
              );
            }

            if (selected && value?.kind === "pinned") {
              return (
                <>
                  <Icon
                    icon={
                      getTemplateIcon(value.template.attributes.icon) ?? faTable
                    }
                    size="xs"
                  />
                  <span className="ml-1 truncate font-inter-normal-12px text-gray-800">
                    {`${value.template.attributes.template_name}, ${value.version.attributes.version_name}`}
                  </span>
                </>
              );
            }

            if (selected && !value) {
              return (
                <>
                  <Icon icon={faTable} size="xs" />
                  <span className="ml-1 truncate text-gray-800">
                    {selectedTemplateName}, {selectedVersionName}
                  </span>
                </>
              );
            }

            return (
              <span className="truncate font-inter-normal-12px text-gray-500">
                {placeholder}
              </span>
            );
          })()}
          <div className="ml-auto flex items-center gap-x-1.5">
            <div className="flex min-h-6 items-center">
              {(() => {
                if (!selected) {
                  return null;
                }

                if (!value) {
                  return <ArchivedPill size="sm" withIcon />;
                }

                if (value.kind === "latest") {
                  return null;
                }

                if (value.version.attributes.publish_state === "published") {
                  return <PublishedPill size="sm" withIcon />;
                }

                return <DraftPill size="sm" withIcon />;
              })()}
            </div>
            {selected && onRefresh && (
              <RefreshButton
                onClick={() =>
                  onRefresh({
                    templateId: selected.templateId,
                    versionId: selected.versionId,
                  })
                }
              />
            )}
            <div
              className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
            >
              <Icon icon={faChevronDown} size="xs" />
            </div>
            {selected && onReset && (
              <>
                <div className="h-4">
                  <Divider orientation="vertical" />
                </div>
                <IconButton
                  dataLoc="case-template-selector-reset"
                  icon={faTimes}
                  size="xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReset();
                  }}
                />
              </>
            )}
          </div>
        </div>
      )}
      renderValue={({ value, selected: isSelected }) => (
        <div className="flex items-center gap-x-3 pl-5">
          {value.kind === "pinned" ? (
            <TemplateVersionName
              name={value.version.attributes.version_name}
              state={value.version.attributes.publish_state}
            />
          ) : (
            <span className="font-inter-normal-12px text-gray-800">
              Always use the last edited published version
            </span>
          )}
          {isSelected && (
            <div className="ml-auto">
              <Icon icon={faCheck} size="xs" variant="active" />
            </div>
          )}
        </div>
      )}
      sections={sections}
      selected={selectedKey}
      listMatchesButtonWidth
      sectionDividers
      onSelect={(key) => {
        const element = elements.find((el) => el.key === key);
        if (element) {
          onSelect({
            templateId: element.value.template.id,
            versionId:
              element.value.kind === "latest"
                ? LATEST_VERSION_ID
                : element.value.version.id,
          });
        }
      }}
    />
  );
};

const RefreshButton = ({ onClick }: { onClick: () => void }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const showRefreshTooltip = searchParams.get(SHOW_REFRESH_TOOLTIP_ON_OPEN);
  const showRefreshTooltipIsDefined = showRefreshTooltip !== null;
  return (
    <Tooltip
      // Switch to uncontrolled tooltip if not defined
      delay={250}
      open={showRefreshTooltipIsDefined ? true : undefined}
      title="Refresh your input schema to apply the latest changes from this version's template"
      asChild
      onOpenChange={
        showRefreshTooltipIsDefined
          ? (open) => {
              if (!open) {
                setSearchParams((prev) => {
                  prev.delete(SHOW_REFRESH_TOOLTIP_ON_OPEN);
                  return prev;
                });
              }
            }
          : undefined
      }
    >
      <IconButton
        icon={faRefresh}
        size="xs"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
      />
    </Tooltip>
  );
};
